import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
    INDIA_ZONE,
    formatIndiaTime,
    timeZoneOptions,
} from '../../helpers/sendSchedule'
import { SYSTEM, TEXT } from '../../theme/tokens'
import Overline from './Overline'

/**
 * "Send at" for an absolute-time event: a date, a time and a searchable time
 * zone (India by default). The date and time are read on the chosen zone's
 * clock, and the India time they equal is shown under them.
 *
 * Holds no state: `value` is `{date, time, zone}` and changes come through
 * `onChange(patch)`; `check` is helpers/sendSchedule `scheduleCheck` of it.
 */
const SendAtPicker = ({ value, onChange, check, zones }) => {
    const options = useMemo(() => zones ?? timeZoneOptions(), [zones])
    let note = null
    if (check.error) {
        note = (
            <Typography
                variant="body2"
                role={check.missing ? undefined : 'alert'}
                sx={{ color: check.missing ? TEXT.faint : SYSTEM.errorSoft }}
            >
                {check.error}
            </Typography>
        )
    } else if (check.epoch) {
        note = (
            <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                {value.zone === INDIA_ZONE
                    ? `Sends on ${formatIndiaTime(check.epoch)}.`
                    : `Sends at that time in ${
                          value.zone
                      }, which is ${formatIndiaTime(check.epoch)}.`}
            </Typography>
        )
    }
    return (
        <Box sx={{ mt: 3 }} data-testid="send-at">
            <Overline sx={{ mb: 0.5 }}>Send at</Overline>
            <Typography variant="body2" sx={{ color: TEXT.faint, mb: 2 }}>
                This event sends at a time you choose. The date and time are
                read in the time zone you pick.
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' },
                    gap: 2,
                    mb: 1.5,
                }}
            >
                <TextField
                    id="send-at-date"
                    label="Date"
                    type="date"
                    value={value.date}
                    onChange={(e) => onChange({ date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                    size="small"
                />
                <TextField
                    id="send-at-time"
                    label="Time"
                    type="time"
                    value={value.time}
                    onChange={(e) => onChange({ time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                    size="small"
                />
                <Autocomplete
                    id="send-at-zone"
                    size="small"
                    options={options}
                    value={value.zone || null}
                    onChange={(_, zone) => onChange({ zone: zone ?? '' })}
                    autoHighlight
                    noOptionsText="No time zone matches"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Time zone"
                            placeholder="Search time zones"
                            required
                        />
                    )}
                />
            </Box>
            {note}
        </Box>
    )
}

SendAtPicker.propTypes = {
    value: PropTypes.shape({
        date: PropTypes.string,
        time: PropTypes.string,
        zone: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    check: PropTypes.shape({
        epoch: PropTypes.number,
        error: PropTypes.string,
        missing: PropTypes.bool,
    }).isRequired,
    // The zones offered; the browser's list by default (tests pass a short one).
    zones: PropTypes.arrayOf(PropTypes.string),
}

export default SendAtPicker
